import { Subject } from 'rxjs';
import { Gizmo } from '../../gizmos';
import { IBinderSetDataParams, IGizmoData, TGizmoData } from '../../types';
import CommonBinder from '../common-binder';

interface IBooleanFieldBinder {
  init(): Promise<void>;
  setValue(value: boolean): Promise<void>;
}

class BooleanFieldBinder extends CommonBinder implements IBooleanFieldBinder {
  public value: Subject<boolean>;

  constructor(gizmo: Gizmo) {
    super(gizmo);

    this.value = new Subject<boolean>();
    this._gizmo.data.subscribe((data: TGizmoData) => {
      if (data && !Array.isArray(data) && typeof data.v === 'number') {
        if (data.v === 4) {
          this.value.next(true);
        } else if (data.v === 0) {
          this.value.next(false);
        }
      }
    });
  }

  public init = (): Promise<void> => {
    return new Promise<void>(async resolve => {
      const config = this._gizmo.getConfig();

      // If the gizmo has no data and we have value configuration we proceed to evalute the Flux
      // script
      if (config.ops?.value?.flux && !this._gizmo.getData()) {
        const flux = this._gizmo.getFlux();

        if (flux) {
          let conditionResult = true;

          if (config.ops.value.condition) {
            const cr = await flux.evaluate({
              script: config.ops.value.condition,
            });

            if (cr !== undefined) {
              conditionResult = cr;
            }
          }

          if (conditionResult === true) {
            const result = await flux.evaluate({
              script: config.ops.value.flux,
            });

            if (result !== undefined && typeof result === 'number') {
              if (result === 4) {
                await this.setValue(true);
              } else if (result === 0) {
                await this.setValue(false);
              }
            }
          }
        }
      }

      resolve();
    });
  };

  /**
   * Function used to set an answer
   *
   * @param {boolean} value value being set by the UI layer
   * @returns
   */
  public setValue = (value?: boolean): Promise<void> => {
    return new Promise<void>(async resolve => {
      if (value === undefined) {
        await this._gizmo.setData(undefined);
        return resolve();
      }

      if (value === true) {
        await this._gizmo.setData({ v: 4 });
      } else {
        await this._gizmo.setData({ v: 0 });
      }

      resolve();
    });
  };

  /**
   * Set data to the gizmo
   * @param data gizmo data to be set
   */
  public setData = async ({ data }: IBinderSetDataParams) => {
    if (
      data.v !== undefined &&
      typeof data.v === 'number' &&
      (data.v === 4 || data.v === 0)
    ) {
      await this._gizmo.setData(data);
    }
  };

  /**
   * Get the value of the gizmo
   *
   * @returns {boolean | undefined} boolean value
   */
  public getValue = (): boolean | undefined => {
    const data = this._gizmo.getData() as IGizmoData;

    if (data?.v !== undefined && typeof data.v === 'number') {
      if (data.v === 4) {
        return true;
      } else if (data.v === 0) {
        return false;
      }
    }

    return undefined;
  };
}

export default BooleanFieldBinder;
